import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const Icon = ({ size = 14, stroke = 1.75, children }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IPlus = (p) => (<Icon {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Icon>);
const IX = (p) => (<Icon {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Icon>);
const IArrow = (p) => (<Icon {...p}><polyline points="9 18 15 12 9 6" /></Icon>);

// === Modale: Nuova organizzazione ============================================
function OrgFormModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', slug: '', contact_email: '', vat_number: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([_, v]) => v));
      const r = await api.post('/api/admin/organizations', payload);
      onSaved(r.data.data);
    } catch (err) {
      const msg = err?.response?.data?.errors
        ? Object.entries(err.response.data.errors).map(([k, v]) => `${k}: ${v[0]}`).join(' · ')
        : err?.response?.data?.message || err?.response?.data?.error || 'Errore salvataggio';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fp__backdrop" onClick={onClose}>
      <aside className="fp__panel" onClick={(e) => e.stopPropagation()} role="dialog">
        <header className="fp__head">
          <div><div className="fp__title">Nuova organizzazione</div></div>
          <button type="button" onClick={onClose} className="fp__close"><IX size={16} /></button>
        </header>
        <form onSubmit={submit} className="fp__body" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {error && <div style={{ background: 'oklch(0.96 0.04 25)', border: '1px solid oklch(0.75 0.12 25)', color: 'oklch(0.35 0.15 25)', padding: '8px 12px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>{error}</div>}
          <section className="fp__section" style={{ paddingTop: 0 }}>
            <div className="fp__field"><label className="fp__lbl">Nome studio *</label>
              <input className="fp__input" required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="fp__field"><label className="fp__lbl">Slug (opzionale, auto da nome)</label>
              <input className="fp__input" placeholder="es. studio-rossi" value={form.slug} onChange={(e) => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} /></div>
            <div className="fp__field"><label className="fp__lbl">Email contatto</label>
              <input type="email" className="fp__input" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} /></div>
            <div className="fp__field"><label className="fp__lbl">P.IVA</label>
              <input className="fp__input" value={form.vat_number} onChange={(e) => setForm(f => ({ ...f, vat_number: e.target.value }))} /></div>
          </section>
        </form>
        <footer className="fp__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn--solid" onClick={submit} disabled={submitting || !form.name}>{submitting ? 'Salvataggio…' : 'Crea organizzazione'}</button>
        </footer>
      </aside>
    </div>
  );
}

// === Modale: Nuovo utente in un'org ==========================================
function UserFormModal({ orgId, orgName, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      const r = await api.post(`/api/admin/organizations/${orgId}/users`, form);
      onSaved(r.data.data);
    } catch (err) {
      const msg = err?.response?.data?.errors
        ? Object.entries(err.response.data.errors).map(([k, v]) => `${k}: ${v[0]}`).join(' · ')
        : err?.response?.data?.message || err?.response?.data?.error || 'Errore salvataggio';
      setError(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fp__backdrop" onClick={onClose}>
      <aside className="fp__panel" onClick={(e) => e.stopPropagation()} role="dialog">
        <header className="fp__head">
          <div>
            <div className="fp__title">Nuovo utente</div>
            <div className="fp__sub">per {orgName}</div>
          </div>
          <button type="button" onClick={onClose} className="fp__close"><IX size={16} /></button>
        </header>
        <form onSubmit={submit} className="fp__body" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {error && <div style={{ background: 'oklch(0.96 0.04 25)', border: '1px solid oklch(0.75 0.12 25)', color: 'oklch(0.35 0.15 25)', padding: '8px 12px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>{error}</div>}
          <section className="fp__section" style={{ paddingTop: 0 }}>
            <div className="fp__field"><label className="fp__lbl">Nome *</label>
              <input className="fp__input" required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="fp__field"><label className="fp__lbl">Email *</label>
              <input type="email" className="fp__input" required value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div className="fp__field"><label className="fp__lbl">Password * (min 8 caratteri)</label>
              <input type="text" className="fp__input" required minLength={8} value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} />
              <small style={{ color: 'var(--fg-muted)', fontSize: 11 }}>Comunicala al referente dello studio. Potrà cambiarla in seguito.</small></div>
          </section>
        </form>
        <footer className="fp__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn--solid" onClick={submit} disabled={submitting || !form.name || !form.email || form.password.length < 8}>{submitting ? 'Salvataggio…' : 'Crea utente'}</button>
        </footer>
      </aside>
    </div>
  );
}

// === Pannello dettaglio org (utenti) =========================================
function OrgDetail({ org, onBack, onChanged }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewUser, setShowNewUser] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/admin/organizations/${org.id}/users`);
      setUsers(data.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [org.id]);

  return (
    <div>
      <button type="button" className="btn btn--ghost" onClick={onBack} style={{ marginBottom: 16 }}>
        <IArrow size={14} style={{ transform: 'rotate(180deg)' }} /> Torna alle organizzazioni
      </button>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{org.name}</h2>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 4 }}>
            slug: <code>{org.slug}</code> · id: {org.id}
            {org.contact_email && ` · ${org.contact_email}`}
            {org.vat_number && ` · P.IVA ${org.vat_number}`}
          </div>
        </div>
        <button type="button" className="btn btn--solid" onClick={() => setShowNewUser(true)}>
          <IPlus size={13} /> Nuovo utente
        </button>
      </div>

      <h3 style={{ fontSize: 14, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Utenti ({users.length})</h3>
      {loading ? <div style={{ color: 'var(--fg-muted)' }}>Caricamento…</div> : (
        users.length === 0 ? (
          <div style={{ padding: 24, background: 'var(--bg-elev)', border: '1px dashed var(--border)', borderRadius: 8, color: 'var(--fg-muted)' }}>
            Nessun utente in questa organizzazione. Creane uno per consentire l'accesso allo studio.
          </div>
        ) : (
          <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            {users.map((u, i) => (
              <div key={u.id} style={{ padding: '12px 16px', borderTop: i > 0 ? '1px solid var(--border)' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{u.email}</div>
                </div>
                {u.is_super_admin && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', background: 'oklch(0.96 0.04 25)', color: 'oklch(0.45 0.18 25)', borderRadius: 4, textTransform: 'uppercase' }}>Super Admin</span>}
              </div>
            ))}
          </div>
        )
      )}

      {showNewUser && (
        <UserFormModal
          orgId={org.id} orgName={org.name}
          onClose={() => setShowNewUser(false)}
          onSaved={() => { setShowNewUser(false); load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

// === Pagina principale =======================================================
export default function AdminOrganizations() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showNewOrg, setShowNewOrg] = useState(false);

  // Guard: solo super-admin. Non-super-admin → redirect a Workflow.
  // (return DOPO le useState per non rompere l'ordine degli hooks.)
  if (user && !user.is_super_admin) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/organizations');
      setOrgs(data.data || []);
    } finally { setLoading(false); }
  };
  // Solo super-admin fanno la fetch (per non-super-admin la Navigate ridirige comunque)
  useEffect(() => { if (user?.is_super_admin) load(); }, [user?.is_super_admin]);

  return (
    <main className="content" style={{ padding: 'clamp(16px, 3vw, 32px)', overflowY: 'auto' }}>
      {selectedOrg ? (
        <OrgDetail org={selectedOrg} onBack={() => setSelectedOrg(null)} onChanged={load} />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Organizzazioni</h1>
              <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--fg-muted)' }}>Gestione studi clienti e relativi utenti</p>
            </div>
            <button type="button" className="btn btn--solid" onClick={() => setShowNewOrg(true)}>
              <IPlus size={13} /> Nuova organizzazione
            </button>
          </div>

          {loading ? <div style={{ color: 'var(--fg-muted)' }}>Caricamento…</div> : (
            orgs.length === 0 ? (
              <div style={{ padding: 32, background: 'var(--bg-elev)', border: '1px dashed var(--border)', borderRadius: 8, color: 'var(--fg-muted)', textAlign: 'center' }}>
                Nessuna organizzazione ancora. Creane una per iniziare.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                {orgs.map((o) => (
                  <button
                    key={o.id} type="button"
                    onClick={() => setSelectedOrg(o)}
                    style={{
                      background: 'var(--bg-elev)', border: '1px solid var(--border)',
                      borderRadius: 12, padding: 18, cursor: 'pointer', textAlign: 'left',
                      display: 'flex', flexDirection: 'column', gap: 8, transition: 'border-color 0.1s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--fg-muted)'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{o.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>slug: <code>{o.slug}</code></div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 'auto', fontSize: 12, color: 'var(--fg-muted)' }}>
                      <span>{o.users_count ?? 0} utenti</span>
                      <span>·</span>
                      <span>{o.leads_count ?? 0} lead</span>
                    </div>
                  </button>
                ))}
              </div>
            )
          )}
        </>
      )}

      {showNewOrg && (
        <OrgFormModal
          onClose={() => setShowNewOrg(false)}
          onSaved={() => { setShowNewOrg(false); load(); }}
        />
      )}
    </main>
  );
}
